import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import Spinner from 'Shared/components/Spinner';
import WpAlert from 'Wpds/components/alert/WpAlert';
import WpToastManager from 'Wpds/components/toast/WpToastManager';
import { useJiraData } from 'Shared/utils/hooks/use-jira-data';
import { createJiraIssue } from 'Shared/actions/todos';
import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import WpSelect from 'Wpds/components/select/WpSelect';
import WpFormLayout from 'Wpds/components/form/WpFormLayout';
import ExternalLinksConstants from 'Shared/constants/ExternalLinks.constants';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

import './CopyToJiraModal.scss';

const componentForField = ({ field, onChange, additionalFieldsValues, tr }) => {
  if (field.schema.type === 'option') {
    return (
      <WpSelect
        id={field.key}
        key={field.key}
        label={field.name}
        options={field.allowedValues.map(({ id, value }) => ({ label: value, value: id }))}
        value={additionalFieldsValues[field.key]}
        placeholder={tr('perform.jira_modal.field_placeholder', field.name)}
        onChange={value => onChange({ id: field.key, value })}
      />
    );
  }
};

type Props = {
  itemUuid: string;
  isOpen: boolean;
  onToggle: () => void;
};

const CopyToJiraModal: React.FC<Props> = ({ itemUuid, isOpen, onToggle }) => {
  const dispatch = useDispatch();
  const { tr } = useTranslation();
  const [project, setProject] = useState(null);
  const [issueType, setIssueType] = useState(null);
  const [additionalFieldsValues, setadditionalFieldsValues] = useState({});
  const [saving, setSaving] = useState(false);
  const jiraData = useJiraData();

  const projects = jiraData?.projects;
  const projectOptions = projects ? projects.map(({ key, name }) => ({ value: key, label: name })) : [];

  // If a project is selected, build the options for the issue types
  const issueTypeOptions = project
    ? projects.find(p => p.key === project).issue_types.map(({ id, name }) => ({ value: id, label: name }))
    : [];

  // If an issue type is selected, collect any required fields for that issue type
  const additionalFields = issueType
    ? projects.find(p => p.key === project).issue_types.find(it => it.id === issueType).required_fields
    : [];
  const showAdditionalFields = additionalFields.length > 0;

  // If this issue type has custom fields that we don't support, we want to show an alert to the user
  const showUnsupportedWarning =
    issueType && !projects.find(p => p.key === project).issue_types.find(it => it.id === issueType).supported;

  // Can only submit if they selected a project, issue type, and we have values for all additional fields
  const canSubmit = !saving && project && issueType && additionalFields.every(f => additionalFieldsValues[f.key]);

  useEffect(() => {
    (window as any).analytics.track('Opened Modal', { modal: 'Copy to Jira' });
  }, []);

  const handleProjectChange = value => {
    setProject(value);
    setIssueType(null);
    setadditionalFieldsValues({});
    return null;
  };

  const handleIssueTypeChange = value => {
    setIssueType(value);
    setadditionalFieldsValues({});
    return null;
  };

  const handleAdditionalFieldChange = ({ id, value }) => {
    setadditionalFieldsValues(Object.assign({}, additionalFieldsValues, { [id]: value }));
  };

  const handleCreateClick = () => {
    setSaving(true);
    dispatch(createJiraIssue({ itemUuid, projectId: project, issueTypeId: issueType, additionalFieldsValues }))
      .then(() => WpToastManager.success(tr('perform.jira_modal.success_message')))
      .catch(() => WpToastManager.danger(tr('perform.jira_modal.error_message')))
      .finally(() => {
        onToggle();
        setSaving(false);
      });
  };

  return (
    <WpModal size="sm" isOpen={isOpen} onToggle={onToggle}>
      <WpModalHeader>{tr('perform.jira_modal.header')}</WpModalHeader>
      <WpModalBody>
        <div className="CreateJiraTaskModal">
          <div>{tr('perform.jira_modal.instruction')}</div>
          <div className="small mb-2 mt-1 text-muted">{tr('perform.jira_modal.subtopics_note')}</div>

          {showUnsupportedWarning && (
            <div className="my-3">
              <WpAlert type="danger">
                {tr('perform.jira_modal.unsupported_warning_prefix')}{' '}
                <a href={ExternalLinksConstants.contact_us} target="_blank" rel="noopener noreferrer">
                  {tr('perform.jira_modal.contact_us')}
                </a>{' '}
                {tr('perform.jira_modal.unsupported_warning_suffix')}
              </WpAlert>
            </div>
          )}

          {jiraData ? (
            <WpFormLayout>
              <WpSelect
                id="project"
                label={tr('perform.jira_modal.project_label')}
                options={projectOptions}
                placeholder={tr('perform.jira_modal.project_placeholder')}
                value={project || ''}
                onChange={handleProjectChange}
              />
              <WpSelect
                id="issue_type"
                label={tr('perform.jira_modal.issue_type_label')}
                options={issueTypeOptions}
                placeholder={tr('perform.jira_modal.issue_type_placeholder')}
                value={issueType || ''}
                onChange={handleIssueTypeChange}
              />
            </WpFormLayout>
          ) : (
            <div className="mt-4 mb-2">
              <Spinner activeIf />
            </div>
          )}
          {showAdditionalFields && (
            <WpFormLayout>
              {additionalFields.map(field =>
                componentForField({ field, onChange: handleAdditionalFieldChange, additionalFieldsValues, tr })
              )}
            </WpFormLayout>
          )}
        </div>
      </WpModalBody>

      <WpModalFooter>
        <WpButtonGroup>
          <WpButton variant="tertiary" disabled={saving} onClick={onToggle}>
            {tr('perform.common.cancel')}
          </WpButton>
          <WpButton disabled={!canSubmit} onClick={handleCreateClick} loading={saving}>
            {tr('perform.jira_modal.create_button')}
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
};

export default CopyToJiraModal;
